/**
 * useFacialRecognition.ts
 * 
 * React hook for facial recognition with MediaPipe
 * Ported from Vue composable for TotalAccess Time & Attendance
 * 
 * Features:
 * - MediaPipe Face Detection with landmarks
 * - Liveness detection (blink + micro-movement)
 * - Distance and lighting guidance
 * - Quality scoring system
 * - Pose estimation for multi-angle enrollment
 * 
 * @author TotalAccess Development Team
 * @date November 2, 2025
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { FaceDetector, FaceLandmarker, FilesetResolver } from '@mediapipe/tasks-vision';
import type { Detection } from '@mediapipe/tasks-vision';
import { loadFaceApiModels } from '@/components/attendance/services/faceApiLoader';
import { isSilentFetchError } from '@/lib/capacitorFetchPatch';

/** Return type shared by callers that build FaceDetector from WASM. */
type VisionWasmFileset = Awaited<ReturnType<typeof FilesetResolver.forVisionTasks>>;

/**
 * MediaPipe evaluates `vision_wasm_internal.js` in-page with a top-level `ModuleFactory`.
 * Loading it twice aborts WASM setup (`ModuleFactory not set`). Two callers can overlap when
 * WASM base paths differ (e.g. `/mediapipe` vs static file CDN) — each keyed load used to run
 * `forVisionTasks` in parallel.
 *
 * One Promise per browsing context guarantees a single WASM runtime initialization.
 */
let visionWasmFilesetSingleton: Promise<VisionWasmFileset> | null = null;
/** Base path supplied to {@link visionWasmFilesetSingleton} first (diagnostics only). */
let visionWasmSingletonBasePath: string | null = null;

/**
 * Some Capacitor WebViews successfully create a GPU-backed MediaPipe detector but then keep
 * returning zero detections for live video. After a sustained zero-detection streak, retry once
 * with a CPU delegate rather than leaving the user stuck in an endless "no face detected" loop.
 */
const ZERO_DETECTION_CPU_RECOVERY_FRAMES = 75;
const DETECTION_STATE_PUBLISH_INTERVAL_MS = 120;
const MEDIAPIPE_CACHE_NAME = 'mediapipe-wasm-v13';

/**
 * FaceLandmarker model candidates, tried in order. The local copy lives in
 * public/mediapipe/ and is precached by the service worker, so it works offline.
 * The static file server + Google bucket are fallbacks for contexts where the
 * local file is missing (e.g. an older deployment).
 */
const FACE_LANDMARKER_MODEL_CANDIDATES = [
  '/mediapipe/face_landmarker.task',
  'https://staticfileserver.totalaccess.co.za/mediapipe/face_landmarker.task',
  // NOTE: upstream face_landmarker.task is the landmarks-only bundle — the
  // blendshapes variant lives under a different filename.
  'https://storage.googleapis.com/mediapipe-assets/face_landmarker_v2_with_blendshapes.task',
];

/**
 * MediaPipe's GPU delegate requires a WebGL context. `FaceDetector.createFromOptions`
 * with `delegate: 'GPU'` does NOT throw when WebGL context creation fails internally —
 * it returns a broken detector that throws `activeTexture` errors on `.detect()`.
 * This pre-check prevents using a GPU delegate when WebGL is unavailable.
 */
function isWebGLAvailable(): boolean {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2') || canvas.getContext('webgl');
    if (!gl) {
      return false;
    }
    // Verify the context is actually functional by attempting a basic operation
    // that MediaPipe's WASM runtime would need (texture creation)
    const testTexture = gl.createTexture();
    if (!testTexture) {
      return false;
    }
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, testTexture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array(4));
    gl.deleteTexture(testTexture);
    return true;
  } catch {
    return false;
  }
}

function loadVisionWasmFilesetOnce(basePath: string): Promise<VisionWasmFileset> {
  const trimmed = basePath.replace(/\/$/, '');
  if (!visionWasmFilesetSingleton) {
    visionWasmSingletonBasePath = trimmed;
    visionWasmFilesetSingleton = FilesetResolver.forVisionTasks(trimmed).catch((err: unknown) => {
      visionWasmFilesetSingleton = null;
      visionWasmSingletonBasePath = null;
      throw err;
    });
    return visionWasmFilesetSingleton;
  }
  if (visionWasmSingletonBasePath !== null && trimmed !== visionWasmSingletonBasePath) {
    console.warn(
      '[FacialRecognition] Reusing in-flight/completed Vision WASM runtime from:',
      visionWasmSingletonBasePath,
      '(alternate path ignored:',
      trimmed + ')'
    );
  }
  return visionWasmFilesetSingleton;
}

// Landmark subset for the non-rigid motion signal — spread across brow, eyes,
// nose, mouth and jaw so coherent deformation is measured, not one region.
const NONRIGID_LANDMARK_INDICES = [
  10, 151, 107, 336,   // forehead / brow top
  33, 133, 362, 263,   // eye corners
  1, 4, 6,             // nose
  61, 291, 13, 14,     // mouth
  0, 17, 152,          // chin / jaw bottom
  234, 454, 50, 280,   // cheeks / jaw sides
  199, 175             // jaw
];

// ==================== CONFIGURATION ====================

export const FACIAL_RECOGNITION_CONFIG = {
  // Quality thresholds (ALL must be met for capture)
  // OPTIMIZED: Lowered thresholds for faster capture while maintaining accuracy
  MIN_PRESENCE: 0.50,     // Face detected and tracked consistently (was 0.6)
  MIN_STABILITY: 0.45,    // Minimal movement (was 0.6) - faster lock-on
  MIN_QUALITY: 0.50,      // Good lighting and clarity (was 0.6)
  MIN_LIVENESS: 0.6,      // Blink detected + micro-movements (unchanged - rarely used)
  MIN_DISTANCE: 0.45,     // Optimal distance from camera (was 0.5)
  MIN_CONFIDENCE: 0.50,   // Detection confidence (was 0.6)
  
  // Distance ranges (face area as % of frame)
  // OPTIMIZED: Wider range for more flexibility
  OPTIMAL_FACE_AREA_MIN: 0.15,  // 15% minimum (was 20%) - allows slightly further
  OPTIMAL_FACE_AREA_MAX: 0.40,  // 40% maximum (was 35%) - allows slightly closer
  
  // Face pose thresholds (degrees) - for enrollment quality
  // Face should be looking mostly straight at camera
  MAX_YAW_DEGREES: 20,          // Max left/right turn (too much = profile view)
  MAX_PITCH_DEGREES: 15,        // Max up/down tilt
  MAX_ROLL_DEGREES: 15,         // Max head tilt
  
  // Liveness detection
  BLINK_EAR_THRESHOLD: 0.2,              // Eye Aspect Ratio threshold (legacy, unused)
  BLINK_REQUIRED_COUNT: 2,               // Require at least 2 blinks (legacy scoring)
  BLINK_RECENCY_TIMEOUT_MS: 2000,        // Blink must be within 2 seconds (legacy scoring)
  BLINK_BLENDSHAPE_CLOSED: 0.5,          // avg eyeBlink blendshape score = eyes closed
  BLINK_BLENDSHAPE_OPEN: 0.35,           // hysteresis: below this = eyes open again
  BLINK_MAX_DURATION_MS: 1000,           // eyes closed longer than this is not a blink
  // Adaptive blink detection: real eyeBlink peaks vary hugely across devices,
  // lighting and users (0.3-0.9), so fixed 0.5/0.35 misses most real blinks.
  // Track a session baseline (EMA of open-eye scores) and trip on relative
  // excursions instead.
  BLINK_BASELINE_ALPHA: 0.08,            // EMA rate per frame (~1s to adapt at 15fps)
  BLINK_CLOSED_DELTA: 0.20,              // score must exceed baseline by this
  BLINK_OPEN_DELTA: 0.10,                // hysteresis: re-open when back near baseline
  BLINK_MIN_CLOSED_ABS: 0.32,            // absolute floor for the closed threshold
  BLINK_MAX_CLOSED_ABS: 0.62,            // absolute ceiling (very elevated baselines)
  FACE_PRESENCE_GAP_TOLERANCE_MS: 500,   // dropped frames tolerated before presence resets
  MICRO_MOVEMENT_VARIANCE_THRESHOLD: 0.8,
  // Passive anti-spoof signals (no user instruction required):
  //  - Facial depth: real faces have large landmark z-spread vs face width;
  //    photos/screens are near-planar.
  //  - Non-rigid motion: a photo translates every landmark identically; a live
  //    face shows per-region displacement dispersion even when "still".
  FACE_DEPTH_MIN_RATIO: 0.22,            // zRange / faceWidth landmark units
  FACE_DEPTH_VOTE_FRAMES: 10,            // window for the depth latch
  FACE_DEPTH_VOTE_MIN: 7,                // frames in window that must pass
  NONRIGID_CV_THRESHOLD: 0.35,           // displacement coefficient of variation
  NONRIGID_MIN_MEAN_PX: 0.6,             // ignore pure detection noise
  NONRIGID_VOTE_FRAMES: 8,
  NONRIGID_VOTE_MIN: 5,
  // DeviceMotion (rotationRate deg/s + accel): a hand-held phone always has
  // micro-tremor; a propped device in front of a photo has none.
  DEVICE_MOTION_WINDOW_MS: 2500,
  DEVICE_MOTION_ROT_RMS: 0.9,            // deg/s RMS of |rotationRate|
  DEVICE_MOTION_ACCEL_RMS: 0.06,         // m/s² RMS of high-passed accel
  DEVICE_MOTION_MIN_SAMPLES: 15,         // need ~0.5s of real sensor data
  
  // Stability
  MAX_MOVEMENT_PIXELS: 8,           // Face center movement threshold (was 5) - more forgiving
  STABILITY_HISTORY_FRAMES: 8,      // ~0.5 second at 15fps (was 15) - FASTER LOCK-ON
  
  // Performance
  TARGET_FPS: 15,                   // Detection frame rate
  HISTORY_DURATION_MS: 3000         // Keep 3 seconds of history (was 5) - faster adaptation
};

// ==================== TYPES ====================

export interface QualityMetrics {
  presence: number;      // 0-1: Face detected and tracked
  stability: number;     // 0-1: Minimal movement
  quality: number;       // 0-1: Good lighting, clear image, frontal pose
  liveness: number;      // 0-1: Blink detected, micro-movements
  distance: number;      // 0-1: Optimal distance
  confidence: number;    // 0-1: Overall detection confidence
  pose?: {               // Face pose estimation
    yaw: number;         // Left/right turn in degrees
    pitch: number;       // Up/down tilt in degrees
    roll: number;        // Head tilt in degrees
    poseScore: number;   // 0-1: How frontal the face is
  };
}

export interface FaceBounds {
  x: number;
  y: number;
  width: number;
  height: number;
  centerX: number;
  centerY: number;
}

export interface LivenessState {
  blinkCount: number;
  lastBlinkTime: number;
  earHistory: number[];
  poseHistory: HeadPose[];
  /** Latched for the whole session — once movement is observed it stays true. */
  microMovementDetected: boolean;
  /** Milliseconds of (near-)continuous face presence this session. */
  facePresenceMs: number;
  /** True while the FaceLandmarker blendshape engine is running. */
  landmarkerActive: boolean;
  /** Latched: landmark z-spread indicates a 3D (non-planar) face. */
  facialDepthOk: boolean;
  /** Latest z-spread / face-width ratio (telemetry for tuning). */
  faceDepthRatio: number;
  /** Latched: per-landmark motion is non-rigid (live face signature). */
  nonRigidMotionDetected: boolean;
  /** Latched: device gyro/accelerometer shows the phone is physically held. */
  deviceMotionDetected: boolean;
  /** Latest device rotation-rate RMS in deg/s (telemetry). */
  deviceMotionLevel: number;
}

export interface HeadPose {
  roll: number;   // Tilt left/right
  pitch: number;  // Nod up/down
  yaw: number;    // Turn left/right
  timestamp: number;
}

export interface DistanceGuidance {
  status: 'too_far' | 'slightly_far' | 'perfect' | 'slightly_close' | 'too_close';
  message: string;
  faceAreaRatio: number;
  optimalMin: number;
  optimalMax: number;
  distanceScore: number;
}

export interface LightingGuidance {
  status: 'too_dark' | 'slightly_dark' | 'good' | 'slightly_bright' | 'too_bright';
  message: string;
  brightness: number;
  optimalMin: number;
  optimalMax: number;
  lightingScore: number;
  hasGlare: boolean;
  hasShadows: boolean;
}

export interface FaceDetectionResult {
  detected: boolean;
  bounds: FaceBounds | null;
  confidence: number;
  landmarks: any[];
  metrics: QualityMetrics;
  liveness: LivenessState;
  distance: DistanceGuidance;
  lighting: LightingGuidance;
  readyToCapture: boolean;
}

interface DetectionHistoryItem {
  timestamp: number;
  bounds: FaceBounds;
  confidence: number;
}

// ==================== TYPES ====================

export type CameraFacingMode = 'user' | 'environment';

export interface UseFacialRecognitionConfig {
  wasmBasePath?: string;
  initialFacingMode?: CameraFacingMode;
  /** Enable FaceLandmarker blendshape liveness from the start (lazy-init on first detection). */
  liveness?: boolean;
}

type ExtendedMediaTrackCapabilities = MediaTrackCapabilities & {
  zoom?: MediaSettingsRange;
};

type ExtendedMediaTrackConstraintSet = MediaTrackConstraintSet & {
  zoom?: number;
};

type ExtendedMediaTrackConstraints = MediaTrackConstraints & {
  advanced?: ExtendedMediaTrackConstraintSet[];
};

const CAMERA_ONE_X_ZOOM_LEVEL = 1;

export function createInitialFaceDetection(): FaceDetectionResult {
  return {
    detected: false,
    bounds: null,
    confidence: 0,
    landmarks: [],
    metrics: {
      presence: 0,
      stability: 0,
      quality: 0,
      liveness: 0,
      distance: 0,
      confidence: 0
    },
    liveness: {
      blinkCount: 0,
      lastBlinkTime: 0,
      earHistory: [],
      poseHistory: [],
      microMovementDetected: false,
      facePresenceMs: 0,
      landmarkerActive: false,
      facialDepthOk: false,
      faceDepthRatio: 0,
      nonRigidMotionDetected: false,
      deviceMotionDetected: false,
      deviceMotionLevel: 0
    },
    distance: {
      status: 'too_far',
      message: 'Move closer to camera',
      faceAreaRatio: 0,
      optimalMin: FACIAL_RECOGNITION_CONFIG.OPTIMAL_FACE_AREA_MIN,
      optimalMax: FACIAL_RECOGNITION_CONFIG.OPTIMAL_FACE_AREA_MAX,
      distanceScore: 0
    },
    lighting: {
      status: 'good',
      message: 'Lighting is adequate',
      brightness: 128,
      optimalMin: 80,
      optimalMax: 180,
      lightingScore: 0,
      hasGlare: false,
      hasShadows: false
    },
    readyToCapture: false
  };
}

function numbersAreClose(a: number, b: number, tolerance = 0.01): boolean {
  return Math.abs(a - b) <= tolerance;
}

function boundsAreEquivalent(left: FaceBounds | null, right: FaceBounds | null, tolerance = 1): boolean {
  if (left === right) {
    return true;
  }

  if (!left || !right) {
    return false;
  }

  return numbersAreClose(left.x, right.x, tolerance)
    && numbersAreClose(left.y, right.y, tolerance)
    && numbersAreClose(left.width, right.width, tolerance)
    && numbersAreClose(left.height, right.height, tolerance)
    && numbersAreClose(left.centerX, right.centerX, tolerance)
    && numbersAreClose(left.centerY, right.centerY, tolerance);
}

function poseIsEquivalent(
  left: QualityMetrics['pose'],
  right: QualityMetrics['pose'],
): boolean {
  if (left === right) {
    return true;
  }

  if (!left || !right) {
    return false;
  }

  return numbersAreClose(left.yaw, right.yaw, 0.75)
    && numbersAreClose(left.pitch, right.pitch, 0.75)
    && numbersAreClose(left.roll, right.roll, 0.75)
    && numbersAreClose(left.poseScore, right.poseScore, 0.02);
}

function hasMeaningfulDetectionChange(prev: FaceDetectionResult, next: FaceDetectionResult): boolean {
  if (prev.detected !== next.detected || prev.readyToCapture !== next.readyToCapture) {
    return true;
  }

  if (!prev.detected && !next.detected) {
    return false;
  }

  return !boundsAreEquivalent(prev.bounds, next.bounds)
    || !numbersAreClose(prev.confidence, next.confidence, 0.01)
    || !numbersAreClose(prev.metrics.presence, next.metrics.presence, 0.01)
    || !numbersAreClose(prev.metrics.stability, next.metrics.stability, 0.01)
    || !numbersAreClose(prev.metrics.quality, next.metrics.quality, 0.01)
    || !numbersAreClose(prev.metrics.liveness, next.metrics.liveness, 0.01)
    || !numbersAreClose(prev.metrics.distance, next.metrics.distance, 0.01)
    || !numbersAreClose(prev.metrics.confidence, next.metrics.confidence, 0.01)
    || !poseIsEquivalent(prev.metrics.pose, next.metrics.pose)
    || prev.distance.status !== next.distance.status
    || prev.distance.message !== next.distance.message
    || !numbersAreClose(prev.distance.faceAreaRatio, next.distance.faceAreaRatio, 0.005)
    || !numbersAreClose(prev.distance.distanceScore, next.distance.distanceScore, 0.01)
    || prev.lighting.status !== next.lighting.status
    || prev.lighting.message !== next.lighting.message
    || !numbersAreClose(prev.lighting.brightness, next.lighting.brightness, 2)
    || !numbersAreClose(prev.lighting.lightingScore, next.lighting.lightingScore, 0.02)
    || prev.lighting.hasGlare !== next.lighting.hasGlare
    || prev.lighting.hasShadows !== next.lighting.hasShadows
    || prev.liveness.blinkCount !== next.liveness.blinkCount
    || prev.liveness.microMovementDetected !== next.liveness.microMovementDetected
    || prev.liveness.landmarkerActive !== next.liveness.landmarkerActive
    || prev.liveness.facialDepthOk !== next.liveness.facialDepthOk
    || prev.liveness.nonRigidMotionDetected !== next.liveness.nonRigidMotionDetected
    || prev.liveness.deviceMotionDetected !== next.liveness.deviceMotionDetected;
}

function shouldPublishDetectionImmediately(prev: FaceDetectionResult, next: FaceDetectionResult): boolean {
  return prev.detected !== next.detected
    || prev.readyToCapture !== next.readyToCapture
    || prev.distance.status !== next.distance.status
    || prev.lighting.status !== next.lighting.status;
}

// ==================== HOOK ====================

export function useFacialRecognition(config?: UseFacialRecognitionConfig) {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isDetecting, setIsDetecting] = useState(false);
  const [facingMode, setFacingMode] = useState<CameraFacingMode>(config?.initialFacingMode || 'user');
  const [currentDetection, setCurrentDetection] = useState<FaceDetectionResult>(() => createInitialFaceDetection());

  // Camera permission state
  const [cameraPermission, setCameraPermission] = useState<'granted' | 'denied' | 'prompt' | 'unknown'>('unknown');
  const [permissionError, setPermissionError] = useState<string | null>(null);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const detectorRef = useRef<FaceDetector | null>(null);
  const detectorDelegateRef = useRef<'GPU' | 'CPU' | null>(null);
  const detectorWasmPathRef = useRef<string | null>(null);
  /** Prevents overlapping initializeDetector runs on one hook instance (race assigns two detectors). */
  const detectorInitInflightRef = useRef<Promise<void> | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const lastDetectionTimeRef = useRef(0);
  const detectionHistoryRef = useRef<DetectionHistoryItem[]>([]);
  const consecutiveErrorsRef = useRef(0);
  const zeroDetectionStreakRef = useRef(0);
  const runtimeRecoveryInFlightRef = useRef<Promise<void> | null>(null);
  const runtimeFallbackAttemptedRef = useRef(false);
  const currentDetectionRef = useRef<FaceDetectionResult>(createInitialFaceDetection());
  const isDetectingRef = useRef(false);
  const lastPublishedDetectionAtRef = useRef(0);

  // FaceLandmarker liveness engine (lazy, opt-in — only runs when a caller
  // enables liveness via config.liveness or enableLiveness()).
  const landmarkerRef = useRef<FaceLandmarker | null>(null);
  const landmarkerInitInflightRef = useRef<Promise<boolean> | null>(null);
  /** Set permanently when landmarker init has definitively failed — avoids retry loops. */
  const landmarkerUnavailableRef = useRef(false);
  const landmarkerErrorStreakRef = useRef(0);
  const livenessEnabledRef = useRef(config?.liveness === true);
  const latestBlinkScoreRef = useRef<number | null>(null);
  const lastLandmarkerTimestampRef = useRef(0);
  const eyesClosedRef = useRef(false);
  const eyesClosedAtRef = useRef(0);
  const facePresentSinceRef = useRef<number | null>(null);
  const lastFaceSeenAtRef = useRef(0);
  // Adaptive blink baseline: EMA of open-eye blendshape scores this session.
  const blinkBaselineRef = useRef(0.1);
  // Latest landmarker geometry (per-frame), consumed by calculateLiveness for
  // the depth/planarity and non-rigid-motion signals.
  const latestLandmarksRef = useRef<Array<{ x: number; y: number; z: number }> | null>(null);
  const prevLandmarksRef = useRef<Array<{ x: number; y: number; z: number }> | null>(null);
  const depthVotesRef = useRef<boolean[]>([]);
  const nonRigidVotesRef = useRef<boolean[]>([]);
  const nonRigidFrameCountRef = useRef(0);
  // DeviceMotion ring buffer: { t, rot (deg/s), lin (m/s² high-passed accel) }.
  const motionSamplesRef = useRef<Array<{ t: number; rot: number; lin: number }>>([]);
  const motionListenerAttachedRef = useRef(false);
  const motionAccelEmaRef = useRef<{ x: number; y: number; z: number } | null>(null);
  const deviceMotionDetectedRef = useRef(false);
  const deviceMotionLevelRef = useRef(0);

  // Check if running in Capacitor
  const isCapacitor = typeof window !== 'undefined' && (
    (window as any).Capacitor?.isNativePlatform?.() ||
    /CapacitorAndroid|CapacitoriOS/.test(navigator.userAgent)
  );

  /**
   * Check camera permission status
   * Uses Permissions API when available, falls back to attempting getUserMedia
   */
  const checkCameraPermission = useCallback(async (): Promise<'granted' | 'denied' | 'prompt'> => {
    try {
      // Try Permissions API first (not all browsers support camera permission query)
      if (navigator.permissions && navigator.permissions.query) {
        try {
          const result = await navigator.permissions.query({ name: 'camera' as PermissionName });
          const status = result.state as 'granted' | 'denied' | 'prompt';
          setCameraPermission(status);
          console.log('[FacialRecognition] Camera permission status:', status);
          return status;
        } catch {
          // Permissions API doesn't support camera query in some browsers
          console.log('[FacialRecognition] Permissions API not supported for camera, will check on access');
        }
      }

      // Fallback: Try to enumerate devices to check camera availability
      if (navigator.mediaDevices?.enumerateDevices) {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const hasCamera = devices.some(d => d.kind === 'videoinput');
        if (!hasCamera) {
          setCameraPermission('denied');
          setPermissionError('No camera found on this device');
          return 'denied';
        }
      }

      // Can't determine, assume prompt
      setCameraPermission('prompt');
      return 'prompt';
    } catch (err) {
      console.error('[FacialRecognition] Error checking camera permission:', err);
      setCameraPermission('unknown');
      return 'prompt';
    }
  }, []);

  /**
   * Open device camera/app settings
   * Uses capacitor-native-settings plugin on mobile
   */
  const openCameraSettings = useCallback(async (): Promise<void> => {
    if (!isCapacitor) {
      console.log('[FacialRecognition] Cannot open settings on non-mobile platform');
      // On web, just show instructions
      setPermissionError('Please enable camera access in your browser settings, then refresh the page.');
      return;
    }

    try {
      // Dynamic import to avoid bundling on web
      const { NativeSettings, AndroidSettings, IOSSettings } = await import('capacitor-native-settings');
      
      // Open app settings (where camera permission can be toggled)
      await NativeSettings.open({
        optionAndroid: AndroidSettings.ApplicationDetails,
        optionIOS: IOSSettings.App
      });
      
      console.log('[FacialRecognition] Opened app settings for camera permission');
    } catch (err: any) {
      console.error('[FacialRecognition] Failed to open settings:', err);
      setPermissionError('Could not open settings. Please go to Settings > Apps > TotalAccess > Permissions and enable Camera.');
    }
  }, [isCapacitor]);

  const disposeCurrentDetector = useCallback(() => {
    if (!detectorRef.current) {
      return;
    }

    try {
      const detector = detectorRef.current as FaceDetector & { close?: () => void };
      detector.close?.();
    } catch (error) {
      console.warn('[FacialRecognition] Detector cleanup warning:', error);
    } finally {
      detectorRef.current = null;
      detectorDelegateRef.current = null;
    }
  }, []);

  /**
   * DeviceMotion sampling for passive liveness. A hand-held phone always has
   * micro-tremor (rotationRate ~1-5°/s RMS); a device propped in front of a
   * photo has none. Listens only while a liveness session is active — no
   * instruction needed, the user just holds the phone naturally.
   */
  const handleDeviceMotion = useCallback((event: DeviceMotionEvent) => {
    const rot = event.rotationRate;
    const acc = event.acceleration ?? event.accelerationIncludingGravity;
    if (!rot && !acc) return;

    const rotMag = rot
      ? Math.sqrt((rot.alpha || 0) ** 2 + (rot.beta || 0) ** 2 + (rot.gamma || 0) ** 2)
      : 0;

    // High-pass the accelerometer (EMA removes gravity) to get linear motion.
    let linMag = 0;
    if (acc && acc.x != null && acc.y != null && acc.z != null) {
      const ema = motionAccelEmaRef.current || { x: acc.x, y: acc.y, z: acc.z };
      linMag = Math.sqrt(
        (acc.x - ema.x) ** 2 + (acc.y - ema.y) ** 2 + (acc.z - ema.z) ** 2
      );
      motionAccelEmaRef.current = {
        x: ema.x + 0.15 * (acc.x - ema.x),
        y: ema.y + 0.15 * (acc.y - ema.y),
        z: ema.z + 0.15 * (acc.z - ema.z)
      };
    }

    const t = performance.now();
    const samples = motionSamplesRef.current;
    samples.push({ t, rot: rotMag, lin: linMag });
    const cutoff = t - FACIAL_RECOGNITION_CONFIG.DEVICE_MOTION_WINDOW_MS;
    while (samples.length > 0 && samples[0].t < cutoff) samples.shift();

    // Rolling RMS over the window — evaluate only once enough samples exist.
    if (samples.length >= FACIAL_RECOGNITION_CONFIG.DEVICE_MOTION_MIN_SAMPLES) {
      let rotSum = 0;
      let linSum = 0;
      for (const s of samples) { rotSum += s.rot * s.rot; linSum += s.lin * s.lin; }
      const rotRms = Math.sqrt(rotSum / samples.length);
      const linRms = Math.sqrt(linSum / samples.length);
      deviceMotionLevelRef.current = rotRms;
      if (
        rotRms >= FACIAL_RECOGNITION_CONFIG.DEVICE_MOTION_ROT_RMS ||
        linRms >= FACIAL_RECOGNITION_CONFIG.DEVICE_MOTION_ACCEL_RMS
      ) {
        if (!deviceMotionDetectedRef.current) {
          console.log('[FacialRecognition] Device motion detected (hand-held signature)');
        }
        deviceMotionDetectedRef.current = true;
      }
    }
  }, []);

  const attachMotionSampling = useCallback(() => {
    if (motionListenerAttachedRef.current || typeof window === 'undefined') return;
    if (!('DeviceMotionEvent' in window)) return;
    window.addEventListener('devicemotion', handleDeviceMotion);
    motionListenerAttachedRef.current = true;
  }, [handleDeviceMotion]);

  /**
   * iOS 13+ requires DeviceMotionEvent.requestPermission() from a user gesture.
   * Called on the next pointerdown automatically (see enableLiveness) — callers
   * may also invoke this directly from a tap/click handler. Resolves true when
   * motion data can be collected (or no permission model exists).
   */
  const requestMotionSensorPermission = useCallback(async (): Promise<boolean> => {
    if (typeof window === 'undefined' || !('DeviceMotionEvent' in window)) return false;
    const DME = window.DeviceMotionEvent as unknown as { requestPermission?: () => Promise<string> };
    if (typeof DME?.requestPermission !== 'function') return true;
    try {
      return (await DME.requestPermission()) === 'granted';
    } catch {
      return false;
    }
  }, []);

  const disposeLandmarker = useCallback(() => {
    if (!landmarkerRef.current) {
      return;
    }

    try {
      landmarkerRef.current.close();
    } catch (error) {
      console.warn('[FacialRecognition] FaceLandmarker cleanup warning:', error);
    } finally {
      landmarkerRef.current = null;
      latestBlinkScoreRef.current = null;
      latestLandmarksRef.current = null;
      prevLandmarksRef.current = null;
      lastLandmarkerTimestampRef.current = 0;
    }
  }, []);

  /**
   * Lazily initialize the FaceLandmarker liveness engine (blendshape blink scores).
   * Idempotent and safe to call concurrently — concurrent callers share the same
   * init promise. Returns whether the engine is now active; when it returns false
   * callers should fall back to the movement/presence-based liveness signal.
   *
   * The model loads through the same MediaPipe WASM runtime as the face detector
   * and is served from /mediapipe/face_landmarker.task (precached by sw.js), so
   * it works fully offline.
   */
  const enableLiveness = useCallback(async (): Promise<boolean> => {
    livenessEnabledRef.current = true;

    // Passive device-motion liveness signal: listen immediately (Android /
    // desktop need no permission) and, on iOS where a user gesture is required
    // to unlock the sensor, arm a one-shot request on the next tap.
    attachMotionSampling();
    if (typeof document !== 'undefined') {
      const DME = (window as { DeviceMotionEvent?: unknown }).DeviceMotionEvent as
        { requestPermission?: () => Promise<string> } | undefined;
      if (typeof DME?.requestPermission === 'function') {
        document.addEventListener(
          'pointerdown',
          () => { void requestMotionSensorPermission(); },
          { once: true }
        );
      }
    }

    if (landmarkerRef.current) {
      return true;
    }

    if (landmarkerUnavailableRef.current) {
      return false;
    }

    if (!landmarkerInitInflightRef.current) {
      landmarkerInitInflightRef.current = (async (): Promise<boolean> => {
        try {
          // Reuse the already-loaded WASM runtime when the detector initialized first.
          const wasmPath = detectorWasmPathRef.current
            || (navigator.onLine ? 'https://staticfileserver.totalaccess.co.za/mediapipe' : '/mediapipe');

          const vision = await loadVisionWasmFilesetOnce(wasmPath);

          const delegateOrder: Array<'GPU' | 'CPU'> = detectorDelegateRef.current === 'GPU'
            ? ['GPU', 'CPU']
            : ['CPU'];

          for (const modelAssetPath of FACE_LANDMARKER_MODEL_CANDIDATES) {
            if (landmarkerRef.current) {
              break;
            }

            for (const delegate of delegateOrder) {
              try {
                const candidate = await FaceLandmarker.createFromOptions(vision, {
                  baseOptions: { modelAssetPath, delegate },
                  runningMode: 'VIDEO',
                  numFaces: 1,
                  outputFaceBlendshapes: true,
                  outputFacialTransformationMatrixes: false,
                  minFaceDetectionConfidence: 0.5,
                  minFacePresenceConfidence: 0.5,
                  minTrackingConfidence: 0.5
                });

                // Smoke test — MediaPipe delegates can return a broken instance
                // without throwing on creation.
                const testCanvas = document.createElement('canvas');
                testCanvas.width = 128;
                testCanvas.height = 128;
                const testCtx = testCanvas.getContext('2d');
                if (testCtx) {
                  testCtx.fillStyle = '#888';
                  testCtx.fillRect(0, 0, 128, 128);
                }
                lastLandmarkerTimestampRef.current = performance.now();
                candidate.detectForVideo(testCanvas, lastLandmarkerTimestampRef.current);

                landmarkerRef.current = candidate;
                console.log(`[FacialRecognition] ✅ FaceLandmarker initialized (${delegate}, ${modelAssetPath})`);
                break;
              } catch (candidateError) {
                console.warn(`[FacialRecognition] FaceLandmarker init failed (${delegate}, ${modelAssetPath}):`, candidateError);
              }
            }
          }

          const active = !!landmarkerRef.current;
          if (!active) {
            landmarkerUnavailableRef.current = true;
            console.warn('[FacialRecognition] FaceLandmarker unavailable on all model paths — using fallback liveness signals');
          }

          // Reflect engine state on the published detection so consumers can
          // see landmarkerActive flip without waiting for the next frame.
          const prev = currentDetectionRef.current;
          const nextDetection: FaceDetectionResult = {
            ...prev,
            liveness: { ...prev.liveness, landmarkerActive: active }
          };
          currentDetectionRef.current = nextDetection;
          setCurrentDetection(nextDetection);

          return active;
        } catch (error) {
          console.error('[FacialRecognition] FaceLandmarker initialization failed:', error);
          landmarkerUnavailableRef.current = true;
          return false;
        } finally {
          landmarkerInitInflightRef.current = null;
        }
      })();
    }

    return landmarkerInitInflightRef.current;
  }, [attachMotionSampling, requestMotionSensorPermission]);

  /**
   * Reset accumulated liveness evidence (blink count, movement latch, presence).
   * Call at the start of a new scan session so evidence does not carry over.
   */
  const resetLiveness = useCallback(() => {
    eyesClosedRef.current = false;
    eyesClosedAtRef.current = 0;
    facePresentSinceRef.current = null;
    lastFaceSeenAtRef.current = 0;
    latestBlinkScoreRef.current = null;
    blinkBaselineRef.current = 0.1;
    latestLandmarksRef.current = null;
    prevLandmarksRef.current = null;
    depthVotesRef.current = [];
    nonRigidVotesRef.current = [];
    nonRigidFrameCountRef.current = 0;
    motionSamplesRef.current = [];
    motionAccelEmaRef.current = null;
    deviceMotionDetectedRef.current = false;
    deviceMotionLevelRef.current = 0;

    const prev = currentDetectionRef.current;
    const nextDetection: FaceDetectionResult = {
      ...prev,
      metrics: { ...prev.metrics, liveness: 0 },
      liveness: {
        blinkCount: 0,
        lastBlinkTime: 0,
        earHistory: [],
        poseHistory: [],
        microMovementDetected: false,
        facePresenceMs: 0,
        landmarkerActive: !!landmarkerRef.current,
        facialDepthOk: false,
        faceDepthRatio: 0,
        nonRigidMotionDetected: false,
        deviceMotionDetected: false,
        deviceMotionLevel: 0
      }
    };
    currentDetectionRef.current = nextDetection;
    setCurrentDetection(nextDetection);
  }, []);

  const normalizeCameraTrackZoom = useCallback(async (track?: MediaStreamTrack | null): Promise<void> => {
    if (!track) {
      return;
    }

    try {
      const capabilities = (track.getCapabilities?.() as ExtendedMediaTrackCapabilities | undefined) || null;
      if (!capabilities?.zoom) {
        return;
      }

      const minZoom = capabilities.zoom.min ?? CAMERA_ONE_X_ZOOM_LEVEL;
      const maxZoom = capabilities.zoom.max ?? CAMERA_ONE_X_ZOOM_LEVEL;
      const targetZoom = Math.min(Math.max(CAMERA_ONE_X_ZOOM_LEVEL, minZoom), maxZoom);

      await track.applyConstraints({
        advanced: [{ zoom: targetZoom }] as ExtendedMediaTrackConstraintSet[]
      } as ExtendedMediaTrackConstraints);

      console.log('[FacialRecognition] Normalized camera zoom to 1x target:', {
        targetZoom,
        minZoom,
        maxZoom,
      });
    } catch (error) {
      console.warn('[FacialRecognition] Unable to normalize camera zoom:', error);
    }
  }, []);

  // Initialize camera
  const initializeCamera = useCallback(async (video: HTMLVideoElement): Promise<void> => {
    try {
      console.log('[FacialRecognition] Initializing camera...');
      console.log('[FacialRecognition] Location:', window.location.protocol + '//' + window.location.hostname);
      console.log('[FacialRecognition] Browser:', navigator.userAgent);
      videoRef.current = video;

      // Check browser support
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        const protocol = window.location.protocol;
        const hostname = window.location.hostname;
        const isSecure = protocol === 'https:' || hostname === 'localhost' || hostname === '127.0.0.1';
        
        throw new Error(
          `Camera API not available. ` +
          `Current URL: ${protocol}//${hostname}. ` +
          `Camera requires HTTPS in production or localhost for development. ` +
          `Secure context: ${isSecure ? 'Yes' : 'No'}. ` +
          `Browser support: ${!!navigator.mediaDevices ? 'Partial' : 'None'}`
        );
      }

      const constraints = {
        video: {
          facingMode: facingMode,
          width: { ideal: 1280, min: 640 },
          height: { ideal: 720, min: 480 },
          aspectRatio: { ideal: 16 / 9 }
        },
        audio: false
      };

      console.log('[FacialRecognition] Requesting camera access with constraints:', constraints);
      let activeStream: MediaStream | null = null;
      
      try {
        activeStream = await navigator.mediaDevices.getUserMedia(constraints);
        streamRef.current = activeStream;
        await normalizeCameraTrackZoom(activeStream.getVideoTracks()[0]);
        // Camera access granted
        setCameraPermission('granted');
        setPermissionError(null);
      } catch (err: any) {
        // Provide user-friendly error messages
        let errorMessage = 'Camera access failed: ';
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          setCameraPermission('denied');
          if (isCapacitor) {
            errorMessage += 'Camera permission denied. Tap "Open Settings" to enable camera access for this app.';
          } else {
            errorMessage += 'Permission denied. Please allow camera access in your browser settings and refresh.';
          }
        } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
          errorMessage += 'No camera found. Please connect a camera and refresh.';
        } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
          // This can also be a permission issue on some Android devices
          if (isCapacitor) {
            setCameraPermission('denied');
            errorMessage += 'Camera access blocked. This may be a permission issue. Tap "Open Settings" to check camera permissions.';
          } else {
            errorMessage += 'Camera is being used by another application. Please close other apps using the camera.';
          }
        } else if (err.name === 'OverconstrainedError') {
          errorMessage += 'Camera does not support the required resolution. Trying lower resolution...';
          console.warn('[FacialRecognition] Retrying with basic constraints...');
          
          // Retry with minimal constraints
          const basicConstraints = { video: { facingMode: facingMode }, audio: false };
          try {
            activeStream = await navigator.mediaDevices.getUserMedia(basicConstraints);
            streamRef.current = activeStream;
            await normalizeCameraTrackZoom(activeStream.getVideoTracks()[0]);
            setCameraPermission('granted');
            setPermissionError(null);
            return; // Success with basic constraints
          } catch (retryErr: any) {
            // Check if retry failed due to permissions
            if (retryErr.name === 'NotAllowedError' || retryErr.name === 'PermissionDeniedError') {
              setCameraPermission('denied');
              errorMessage = 'Camera permission denied. Tap "Open Settings" to enable camera access.';
            } else {
              errorMessage += ' Retry also failed: ' + (retryErr.message || 'Unknown error');
            }
          }
        } else if (err.name === 'TypeError') {
          errorMessage += 'Browser security error. Camera requires HTTPS in production.';
        } else {
          errorMessage += err.message || 'Unknown error';
        }
        
        setPermissionError(errorMessage);
        throw new Error(errorMessage);
      }
      
      // CRITICAL: Check that video element still exists before assigning stream
      // React StrictMode double-invokes effects, which can cause video to be null
      if (!video || !videoRef.current) {
        console.warn('[FacialRecognition] Video element no longer available, stopping camera stream');
        if (activeStream) {
          activeStream.getTracks().forEach(track => track.stop());
          if (streamRef.current === activeStream) {
            streamRef.current = null;
          }
        }
        throw new Error('Video element was unmounted during camera initialization');
      }
      
      video.srcObject = activeStream;
      // Handle AbortError gracefully - happens when autoPlay conflicts with manual play()
      try {
        await video.play();
      } catch (playError: any) {
        if (playError.name === 'AbortError') {
          console.log('[FacialRecognition] Play interrupted (autoPlay conflict) - video will continue via autoPlay');
        } else {
          throw playError;
        }
      }

      console.log('[FacialRecognition] Camera initialized successfully');
      const activeTrack = activeStream?.getVideoTracks?.()[0];
      console.log('[FacialRecognition] Video track settings:', activeTrack?.getSettings?.());
    } catch (error: any) {
      console.error('[FacialRecognition] Camera initialization failed:', error);
      throw error;
    }
  }, [facingMode, isCapacitor, normalizeCameraTrackZoom]);

  // Initialize MediaPipe detector
  const initializeDetector = useCallback(async (customWasmPath?: string, preferredDelegate?: 'GPU' | 'CPU'): Promise<void> => {
    if (detectorRef.current && (!preferredDelegate || detectorDelegateRef.current === preferredDelegate)) {
      setIsInitialized(true);
      return;
    }

    if (detectorRef.current && preferredDelegate && detectorDelegateRef.current !== preferredDelegate) {
      disposeCurrentDetector();
      setIsInitialized(false);
    }

    if (!detectorInitInflightRef.current) {
      detectorInitInflightRef.current = (async (): Promise<void> => {
        try {
      console.log('[FacialRecognition] Initializing MediaPipe Face Detector...');

      // Load face-api.js models in parallel with MediaPipe
      loadFaceApiModels().catch(err => {
        if (!isSilentFetchError(err)) console.warn('[FacialRecognition] face-api.js models failed to load, will use fallback:', err);
      });

      // CRITICAL: For offline support in Capacitor apps, use LOCAL MediaPipe files
      // Cross-origin requests to staticfileserver.totalaccess.co.za don't get intercepted
      // by the service worker when offline, causing WebAssembly loading to fail.
      // Local files at /mediapipe/ are served from the same origin and work offline.
      const STATIC_FILE_SERVER = 'https://staticfileserver.totalaccess.co.za';
      
      // Determine the best WASM path based on context
      let WASM_PATH: string;
      
      if (customWasmPath) {
        WASM_PATH = customWasmPath;
      } else if (config?.wasmBasePath) {
        WASM_PATH = config.wasmBasePath;
      } else if (!navigator.onLine) {
        // OFFLINE: Always use local files - cross-origin won't work
        WASM_PATH = '/mediapipe';
        console.log('[FacialRecognition] OFFLINE - Using local MediaPipe files');
      } else if (typeof window !== 'undefined' && 'Capacitor' in window) {
        // Capacitor: Use local files for reliable offline support
        WASM_PATH = '/mediapipe';
        console.log('[FacialRecognition] Capacitor - Using local MediaPipe files for offline support');
      } else {
        // Web: Can use static file server (has CORS configured)
        WASM_PATH = `${STATIC_FILE_SERVER}/mediapipe`;
      }

      if (WASM_PATH.endsWith('/')) {
        WASM_PATH = WASM_PATH.slice(0, -1);
      }

      detectorWasmPathRef.current = WASM_PATH;

      console.log('[FacialRecognition] Loading MediaPipe from:', WASM_PATH);
      console.log('[FacialRecognition] Service Worker active:', !!navigator.serviceWorker?.controller);
      console.log('[FacialRecognition] Online status:', navigator.onLine);
      console.log('[FacialRecognition] Using local MediaPipe:', WASM_PATH.startsWith('/'));

      // Check if service worker is caching MediaPipe files
      if (!navigator.serviceWorker?.controller) {
        console.warn('[FacialRecognition] ⚠️  Service worker not active - MediaPipe may not work offline');
      }

      // Test if WASM files are accessible
      // CRITICAL: When offline, check cache directly instead of making HEAD requests
      // HEAD requests are not intercepted by the service worker and will fail offline
      const testFile = 'vision_wasm_internal.js';
      const testUrl = `${WASM_PATH}/${testFile}`;
      
      if (!navigator.onLine) {
        // OFFLINE: Check service worker cache directly
        console.log('[FacialRecognition] OFFLINE - Checking MediaPipe cache...');
        console.log('[FacialRecognition] Looking for:', testUrl);
        
        // Define all possible locations for MediaPipe files
        const STATIC_SERVER = 'https://staticfileserver.totalaccess.co.za';
        const possibleUrls = [
          testUrl,
          `${window.location.origin}${testUrl}`,
          `${window.location.origin}/mediapipe/${testFile}`,
          `/mediapipe/${testFile}`,
          `${STATIC_SERVER}/mediapipe/${testFile}`,
        ];
        
        try {
          const cache = await caches.open(MEDIAPIPE_CACHE_NAME);
          
          // Log all cache keys for debugging
          const allKeys = await cache.keys();
          console.log('[FacialRecognition] MediaPipe cache has', allKeys.length, 'entries');
          if (allKeys.length > 0) {
            console.log('[FacialRecognition] Cache keys:', allKeys.map(k => k.url));
          }
          
          // Try all possible URL patterns
          let cachedResponse = null;
          
          for (const url of possibleUrls) {
            cachedResponse = await cache.match(url);
            if (cachedResponse) {
              console.log('[FacialRecognition] ✅ Found cached file at:', url);
              break;
            }
          }
          
          // Also check if ANY MediaPipe file is in cache (remote URL might be cached)
          if (!cachedResponse && allKeys.length > 0) {
            const hasRemoteWasm = allKeys.some(k => 
              k.url.includes('vision_wasm_internal.js') || 
              k.url.includes('vision_wasm_internal.wasm')
            );
            
            if (hasRemoteWasm) {
              console.log('[FacialRecognition] ✅ Found remote MediaPipe files in cache');
              // Switch WASM_PATH to use the cached remote files
              const remoteKey = allKeys.find(k => k.url.includes('vision_wasm_internal.js'));
              if (remoteKey) {
                const remoteBase = remoteKey.url.replace('/vision_wasm_internal.js', '');
                console.log('[FacialRecognition] Switching to cached remote path:', remoteBase);
                WASM_PATH = remoteBase;
              }
              cachedResponse = true; // Mark as found
            }
          }
          
          if (cachedResponse) {
            console.log('[FacialRecognition] ✅ WASM files available for offline use');
          } else {
            console.error('[FacialRecognition] ❌ WASM files not in cache. Searched URLs:', possibleUrls);
            throw new Error(
              'MediaPipe WASM files not cached for offline use. ' +
              'Please connect to the internet, click "Prepare Offline Mode", and try again.'
            );
          }
        } catch (cacheError: any) {
          if (cacheError.message?.includes('not cached')) {
            throw cacheError;
          }
          console.error('[FacialRecognition] Cache check failed:', cacheError);
          throw new Error(
            'Cannot verify MediaPipe cache. ' +
            'Please connect to the internet and reload the page.'
          );
        }
      } else {
        // ONLINE: Use GET request (not HEAD) so service worker can cache it
        try {
          console.log('[FacialRecognition] Testing WASM accessibility:', testUrl);
          const response = await fetch(testUrl, { method: 'GET' });
          if (!response.ok) {
            throw new Error(`WASM files not accessible: ${response.status} ${response.statusText}`);
          }
          console.log('[FacialRecognition] ✅ WASM files are accessible');
        } catch (fetchError: any) {
          console.error('[FacialRecognition] ❌ WASM files not accessible:', fetchError);
          throw new Error(
            `MediaPipe WASM files cannot be loaded from ${WASM_PATH}. ` +
            `Error: ${fetchError.message}. ` +
            `This may be due to CORS issues or missing files on the server.`
          );
        }
      }

      let vision;
      try {
        console.log('[FacialRecognition] Calling FilesetResolver.forVisionTasks with path:', WASM_PATH);
        console.log('[FacialRecognition] Online status before FilesetResolver:', navigator.onLine);
        vision = await loadVisionWasmFilesetOnce(WASM_PATH);
        console.log('[FacialRecognition] ✅ FilesetResolver loaded');
      } catch (resolverError: any) {
        console.error('[FacialRecognition] FilesetResolver failed:', resolverError);
        console.error('[FacialRecognition] FilesetResolver error details:', {
          name: resolverError.name,
          message: resolverError.message,
          stack: resolverError.stack?.substring(0, 500)
        });
        
        // When offline, try to provide more helpful context
        if (!navigator.onLine) {
          // Check what's in the MediaPipe cache
          try {
            const cache = await caches.open(MEDIAPIPE_CACHE_NAME);
            const keys = await cache.keys();
            console.log('[FacialRecognition] MediaPipe cache contents:', keys.map(k => k.url));
          } catch (cacheErr) {
            console.error('[FacialRecognition] Failed to check cache:', cacheErr);
          }
        }
        
        throw new Error(
          `Failed to load MediaPipe vision tasks: ${resolverError.message}. ` +
          `WASM path: ${WASM_PATH}. Online: ${navigator.onLine}`
        );
      }

      // Use model file from the same source as WASM files
      // This ensures consistent offline behavior
      let modelPath = WASM_PATH.includes('staticfileserver') 
        ? `${STATIC_FILE_SERVER}/mediapipe/blaze_face_short_range.tflite`
        : '/mediapipe/blaze_face_short_range.tflite';

      console.log('[FacialRecognition] Initial model path:', modelPath);

      // Test if model file is accessible
      // CRITICAL: When offline, check cache directly instead of making HEAD requests
      if (!navigator.onLine) {
        // OFFLINE: Check service worker cache directly
        console.log('[FacialRecognition] OFFLINE - Checking model file cache...');
        try {
          const cache = await caches.open(MEDIAPIPE_CACHE_NAME);
          
          // First, log ALL keys in the cache for debugging
          const allCacheKeys = await cache.keys();
          console.log('[FacialRecognition] MediaPipe cache has', allCacheKeys.length, 'entries');
          console.log('[FacialRecognition] All cache keys:', allCacheKeys.map(k => k.url));
          
          // Try multiple possible URLs for the model file
          const possibleModelUrls = [
            modelPath,
            `${window.location.origin}${modelPath}`,
            '/mediapipe/blaze_face_short_range.tflite',
            `${window.location.origin}/mediapipe/blaze_face_short_range.tflite`,
            `${STATIC_FILE_SERVER}/mediapipe/blaze_face_short_range.tflite`,
          ];
          
          console.log('[FacialRecognition] Looking for model at:', possibleModelUrls);
          
          let cachedResponse = null;
          let foundModelUrl = '';
          
          for (const url of possibleModelUrls) {
            console.log('[FacialRecognition] Trying URL:', url);
            cachedResponse = await cache.match(url);
            if (cachedResponse) {
              foundModelUrl = url;
              console.log('[FacialRecognition] ✅ Found at:', url);
              break;
            }
          }
          
          // Also check all cache keys for model file
          if (!cachedResponse) {
            console.log('[FacialRecognition] Direct URL match failed, checking by filename...');
            const modelKey = allCacheKeys.find(k => k.url.includes('blaze_face_short_range.tflite'));
            if (modelKey) {
              console.log('[FacialRecognition] Found key by filename:', modelKey.url);
              cachedResponse = await cache.match(modelKey);
              foundModelUrl = modelKey.url;
            }
          }
          
          if (cachedResponse) {
            console.log('[FacialRecognition] ✅ Model file found in cache at:', foundModelUrl);
            // Update modelPath to use the cached URL
            if (foundModelUrl && foundModelUrl !== modelPath) {
              console.log('[FacialRecognition] Switching model path to cached URL:', foundModelUrl);
              modelPath = foundModelUrl;
            }
          } else {
            console.error('[FacialRecognition] ❌ Model file not in cache');
            console.error('[FacialRecognition] Searched URLs:', possibleModelUrls);
            throw new Error(
              'Face detection model not cached for offline use. ' +
              'Please connect to the internet once to download the required files.'
            );
          }
        } catch (cacheError: any) {
          if (cacheError.message?.includes('not cached')) {
            throw cacheError;
          }
          console.error('[FacialRecognition] Model cache check failed:', cacheError);
          throw new Error(
            'Cannot verify model cache. ' +
            'Please connect to the internet and reload the page.'
          );
        }
      } else {
        // ONLINE: Use GET request (not HEAD) so service worker can cache it
        try {
          const modelResponse = await fetch(modelPath, { method: 'GET' });
          if (!modelResponse.ok) {
            throw new Error(`Model file not accessible: ${modelResponse.status} ${modelResponse.statusText}`);
          }
          console.log('[FacialRecognition] ✅ Model file is accessible');
        } catch (modelFetchError: any) {
          console.error('[FacialRecognition] ❌ Model file not accessible:', modelFetchError);
          throw new Error(
            `Face detection model cannot be loaded from ${modelPath}. ` +
            `Error: ${modelFetchError.message}`
          );
        }
      }

      // Use IMAGE mode (more reliable than VIDEO mode)
      // GPU delegate is only used on Capacitor (mobile) where it provides performance
      // benefits. On portal/desktop browsers, MediaPipe's GPU delegate can silently fail
      // (returns a broken detector that throws 'activeTexture' errors on .detect()).
      // CPU is fast enough on desktop and more reliable.
      const useGpu = preferredDelegate === 'GPU'
        || (preferredDelegate !== 'CPU' && isCapacitor && isWebGLAvailable());

      if (!useGpu && preferredDelegate !== 'CPU') {
        console.log('[FacialRecognition] Using CPU delegate (non-Capacitor or WebGL unavailable)');
      }

      const delegateOrder: Array<'GPU' | 'CPU'> = useGpu
        ? ['GPU', 'CPU']
        : ['CPU'];

      let lastDelegateError: Error | null = null;

      for (const delegate of delegateOrder) {
        try {
          const candidate = await FaceDetector.createFromOptions(vision, {
            baseOptions: {
              modelAssetPath: modelPath,
              delegate,
            },
            runningMode: 'IMAGE',
            minDetectionConfidence: 0.5
          });

          // CRITICAL: MediaPipe GPU delegate does NOT throw on creation even when
          // WebGL context creation fails internally. It returns a broken detector
          // that throws 'activeTexture' errors on .detect(). Test immediately with
          // a dummy image to verify the detector actually works.
          try {
            const testCanvas = document.createElement('canvas');
            testCanvas.width = 128;
            testCanvas.height = 128;
            const testCtx = testCanvas.getContext('2d');
            if (testCtx) {
              testCtx.fillStyle = '#888';
              testCtx.fillRect(0, 0, 128, 128);
            }
            await candidate.detect(testCanvas);
          } catch (testError) {
            // Detector is broken — dispose it and try next delegate
            try {
              (candidate as FaceDetector & { close?: () => void }).close?.();
            } catch { /* ignore */ }
            throw testError;
          }

          detectorRef.current = candidate;
          detectorDelegateRef.current = delegate;
          console.log(`[FacialRecognition] ✅ Detector created and verified with ${delegate} delegate`);
          break;
        } catch (delegateError: unknown) {
          lastDelegateError = delegateError instanceof Error
            ? delegateError
            : new Error(String(delegateError));

          if (delegate === 'GPU' && delegateOrder.includes('CPU')) {
            console.warn('[FacialRecognition] GPU delegate failed (broken or unavailable), trying CPU:', delegateError);
            continue;
          }

          // CPU delegate also failed — check if it's the WebGL activeTexture issue
          const errMsg = lastDelegateError.message || '';
          if (errMsg.includes('activeTexture') || errMsg.includes('emscripten_webgl_create_context')) {
            throw new Error(
              'Face detection unavailable: WebGL context creation failed. ' +
              'MediaPipe requires WebGL even in CPU mode for texture processing. ' +
              'Try: 1) Restart your browser, 2) Enable hardware acceleration in browser settings, ' +
              '3) Update your graphics drivers.'
            );
          }

          throw lastDelegateError;
        }
      }

      if (!detectorRef.current) {
        const delegateLabel = delegateOrder.join(' then ');
        throw new Error(
          `Failed to create face detector using ${delegateLabel}: ${lastDelegateError?.message || 'Unknown error'}`
        );
      }

      console.log('[FacialRecognition] ✅ Detector initialized successfully');
      setIsInitialized(true);
      zeroDetectionStreakRef.current = 0;

      // Kick off FaceLandmarker init in the background when a caller already
      // asked for liveness — it shares this WASM runtime and warms the model
      // before the first frame arrives.
      if (livenessEnabledRef.current) {
        void enableLiveness();
      }

    } catch (error: any) {
      console.error('[FacialRecognition] ❌ Detector initialization failed:', error);
      const errorMsg = error.message || error.toString();
      
      // Log additional context for debugging
      console.error('[FacialRecognition] Error context:', {
        online: navigator.onLine,
        serviceWorker: !!navigator.serviceWorker?.controller,
        errorMessage: errorMsg
      });
      
      // Provide helpful error messages based on error type
      if (errorMsg.includes('Failed to fetch') || errorMsg.includes('NetworkError') || errorMsg.includes('Load failed')) {
        if (!navigator.onLine) {
          // When offline, the issue is likely cache mismatch
          throw new Error(
            'Cannot load MediaPipe models offline. The models may not be properly cached. ' +
            'Please: 1) Connect to the internet, 2) Click "Prepare Offline Mode" to re-cache models, ' +
            '3) Try again offline.'
          );
        } else if (!navigator.serviceWorker?.controller) {
          throw new Error(
            'Cannot load MediaPipe models: No internet connection and service worker not active. ' +
            'Please reload the page while online to enable offline support.'
          );
        } else {
          throw new Error(
            'Cannot load MediaPipe models: Network error. ' +
            'The service worker should cache these files - please check your internet connection ' +
            'and reload the page.'
          );
        }
      }
      
      if (errorMsg.includes('503') || errorMsg.includes('Service Unavailable')) {
        throw new Error(
          'MediaPipe models not cached for offline use. ' +
          'Please connect to the internet once to download the required files.'
        );
      }
      
      throw error;
    } finally {
      detectorInitInflightRef.current = null;
    }
      })();
    }
    await detectorInitInflightRef.current;
  }, [config?.wasmBasePath, disposeCurrentDetector, enableLiveness, isCapacitor]);

  // Stop camera
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    setIsDetecting(false);
    isDetectingRef.current = false;
    zeroDetectionStreakRef.current = 0;
    consecutiveErrorsRef.current = 0;
    detectionHistoryRef.current = [];
    lastPublishedDetectionAtRef.current = 0;
    console.log('[FacialRecognition] Camera stopped');
  }, []);

  /**
   * Calculate face pose from MediaPipe keypoints
   * MediaPipe Short Range provides 6 keypoints:
   * 0: right eye, 1: left eye, 2: nose tip, 3: mouth center, 4: right ear, 5: left ear
   */
  const calculateFacePose = useCallback((keypoints: any[], bounds: FaceBounds): { yaw: number; pitch: number; roll: number; poseScore: number } => {
    // Default values if we can't calculate
    const defaultPose = { yaw: 0, pitch: 0, roll: 0, poseScore: 1 };
    
    if (!keypoints || keypoints.length < 4) {
      return defaultPose;
    }
    
    try {
      // MediaPipe keypoint indices (may vary - check for named keypoints)
      // Common indices: 0=right eye, 1=left eye, 2=nose tip, 3=mouth center
      const getKeypoint = (name: string, fallbackIdx: number) => {
        const named = keypoints.find(k => k.label === name || k.name === name);
        return named || keypoints[fallbackIdx];
      };
      
      const rightEye = getKeypoint('right_eye', 0);
      const leftEye = getKeypoint('left_eye', 1);
      const noseTip = getKeypoint('nose_tip', 2);
      
      if (!rightEye || !leftEye || !noseTip) {
        return defaultPose;
      }
      
      // Get coordinates (MediaPipe returns normalized 0-1 or pixel coords)
      const getCoord = (kp: any) => ({
        x: kp.x ?? kp.xNorm ?? 0,
        y: kp.y ?? kp.yNorm ?? 0
      });
      
      const re = getCoord(rightEye);
      const le = getCoord(leftEye);
      const nose = getCoord(noseTip);
      
      // Calculate face dimensions for reference
      const eyeDistance = Math.sqrt(Math.pow(le.x - re.x, 2) + Math.pow(le.y - re.y, 2));
      const eyeMidX = (le.x + re.x) / 2;
      const eyeMidY = (le.y + re.y) / 2;
      
      // YAW (left/right turn): measured by nose position relative to eye midpoint
      // If nose is to the left of eye midpoint, face is turned right (positive yaw)
      // Normalize by eye distance to get angle estimate
      const noseOffset = (nose.x - eyeMidX) / Math.max(eyeDistance, 0.01);
      // Convert to approximate degrees (empirical: 0.5 offset ≈ 30 degrees)
      const yaw = noseOffset * 60;
      
      // PITCH (up/down): measured by vertical nose position relative to eyes
      const noseVerticalOffset = (nose.y - eyeMidY) / Math.max(eyeDistance, 0.01);
      // Normal ratio is about 0.5-0.7 (nose below eyes). Deviation indicates pitch
      const expectedRatio = 0.6;
      const pitch = (noseVerticalOffset - expectedRatio) * 50;
      
      // ROLL (head tilt): angle between eye line and horizontal
      const eyeAngle = Math.atan2(le.y - re.y, le.x - re.x);
      const roll = eyeAngle * (180 / Math.PI);
      
      // Calculate pose score (1 = looking straight, 0 = looking away)
      const yawPenalty = Math.abs(yaw) / FACIAL_RECOGNITION_CONFIG.MAX_YAW_DEGREES;
      const pitchPenalty = Math.abs(pitch) / FACIAL_RECOGNITION_CONFIG.MAX_PITCH_DEGREES;
      const rollPenalty = Math.abs(roll) / FACIAL_RECOGNITION_CONFIG.MAX_ROLL_DEGREES;
      
      // Use max penalty but cap individual penalties at 1
      const maxPenalty = Math.max(
        Math.min(yawPenalty, 2),
        Math.min(pitchPenalty, 2),
        Math.min(rollPenalty, 2)
      );
      
      // Convert to score: 0 penalty = 1.0, 1+ penalty = reduced score
      const poseScore = Math.max(0, 1 - (maxPenalty * 0.5));
      
      return { yaw, pitch, roll, poseScore };
    } catch (error) {
      console.warn('[FacialRecognition] Pose calculation failed:', error);
      return defaultPose;
    }
  }, []);

  // Calculate quality metrics
  const calculateQualityMetrics = useCallback((bounds: FaceBounds, confidence: number, keypoints?: any[]): QualityMetrics => {
    const history = detectionHistoryRef.current;

    // Presence: based on detection history consistency
    const presence = Math.min(1, history.length / FACIAL_RECOGNITION_CONFIG.STABILITY_HISTORY_FRAMES);

    // Stability: average movement between frames
    let stability = 0;
    if (history.length > 1) {
      let totalMovement = 0;
      for (let i = 1; i < history.length; i++) {
        const prev = history[i - 1].bounds;
        const curr = history[i].bounds;
        const dx = curr.centerX - prev.centerX;
        const dy = curr.centerY - prev.centerY;
        totalMovement += Math.sqrt(dx * dx + dy * dy);
      }
      const avgMovement = totalMovement / (history.length - 1);
      stability = avgMovement < FACIAL_RECOGNITION_CONFIG.MAX_MOVEMENT_PIXELS ? 1 :
        Math.max(0, 1 - (avgMovement - FACIAL_RECOGNITION_CONFIG.MAX_MOVEMENT_PIXELS) / 20);
    }

    // Calculate face pose from keypoints
    const pose = calculateFacePose(keypoints || [], bounds);

    // Distance score: based on face size relative to frame
    const frameWidth = videoRef.current?.videoWidth || 1280;
    const frameHeight = videoRef.current?.videoHeight || 720;
    const frameArea = frameWidth * frameHeight;
    const faceArea = bounds.width * bounds.height;
    const areaRatio = faceArea / frameArea;

    // Log occasionally for debugging
    if (Math.random() < 0.05) {
      console.log('[FacialRecognition] Quality calculation:', {
        areaRatio: areaRatio.toFixed(4),
        pose: { yaw: pose.yaw.toFixed(1), pitch: pose.pitch.toFixed(1), roll: pose.roll.toFixed(1), score: pose.poseScore.toFixed(2) },
        confidence: confidence.toFixed(2)
      });
    }

    // Distance score (face size in frame)
    let distanceScore = 0;
    if (areaRatio >= FACIAL_RECOGNITION_CONFIG.OPTIMAL_FACE_AREA_MIN &&
        areaRatio <= FACIAL_RECOGNITION_CONFIG.OPTIMAL_FACE_AREA_MAX) {
      distanceScore = 1;
    } else if (areaRatio < FACIAL_RECOGNITION_CONFIG.OPTIMAL_FACE_AREA_MIN) {
      distanceScore = areaRatio / FACIAL_RECOGNITION_CONFIG.OPTIMAL_FACE_AREA_MIN;
    } else {
      distanceScore = Math.max(0, 1 - (areaRatio - FACIAL_RECOGNITION_CONFIG.OPTIMAL_FACE_AREA_MAX) / 0.35);
    }

    // QUALITY: Combination of distance, pose, and confidence
    // All factors contribute - poor pose should NOT result in 100% quality
    const poseWeight = 0.4;      // Face orientation matters a lot for enrollment
    const distanceWeight = 0.3;  // Distance/size in frame
    const confidenceWeight = 0.3; // Detection confidence
    
    const quality = Math.min(1, 
      (pose.poseScore * poseWeight) + 
      (distanceScore * distanceWeight) + 
      (confidence * confidenceWeight)
    );

    // Liveness score from actually-observed signals this session.
    // A blink is the strong signal; 3D facial geometry (depth/non-rigid motion)
    // is nearly as strong; device-hand-held motion and in-frame micro-movement
    // are weaker corroborating evidence. All latches are session-scoped (see
    // calculateLiveness), so the score reflects "has this person proven live
    // at any point", not the instantaneous frame — a user holding still for
    // capture doesn't lose it.
    const livenessState = currentDetectionRef.current.liveness;
    const hasBlink = livenessState.blinkCount >= 1;
    const hasMicroMovement = livenessState.microMovementDetected;
    const has3DGeometry = livenessState.facialDepthOk || livenessState.nonRigidMotionDetected;
    const hasDeviceMotion = livenessState.deviceMotionDetected;

    let liveness = 0;
    if (hasBlink && hasMicroMovement) {
      liveness = 1.0;
    } else if (hasBlink) {
      liveness = 0.7;
    } else if (has3DGeometry && hasMicroMovement) {
      liveness = 0.85;
    } else if (has3DGeometry) {
      liveness = 0.55;
    } else if (hasDeviceMotion && hasMicroMovement) {
      liveness = 0.5;
    } else if (hasMicroMovement) {
      liveness = 0.4;
    } else if (hasDeviceMotion) {
      liveness = 0.3;
    }

    return {
      presence,
      stability,
      quality,
      liveness,
      distance: distanceScore,
      confidence,
      pose
    };
  }, [calculateFacePose]);

  // Calculate distance guidance
  const calculateDistanceGuidance = useCallback((bounds: FaceBounds): DistanceGuidance => {
    const frameWidth = videoRef.current?.videoWidth || 1280;
    const frameHeight = videoRef.current?.videoHeight || 720;
    const frameArea = frameWidth * frameHeight;
    const faceArea = bounds.width * bounds.height;
    const areaRatio = faceArea / frameArea;

    const optimalMin = FACIAL_RECOGNITION_CONFIG.OPTIMAL_FACE_AREA_MIN;
    const optimalMax = FACIAL_RECOGNITION_CONFIG.OPTIMAL_FACE_AREA_MAX;

    let status: DistanceGuidance['status'];
    let message: string;
    let distanceScore: number;

    if (areaRatio < optimalMin * 0.7) {
      status = 'too_far';
      message = 'Move much closer';
      distanceScore = areaRatio / optimalMin;
    } else if (areaRatio < optimalMin) {
      status = 'slightly_far';
      message = 'Move a bit closer';
      distanceScore = 0.8;
    } else if (areaRatio <= optimalMax) {
      status = 'perfect';
      message = 'Perfect distance!';
      distanceScore = 1.0;
    } else if (areaRatio <= optimalMax * 1.3) {
      status = 'slightly_close';
      message = 'Move back slightly';
      distanceScore = 0.8;
    } else {
      status = 'too_close';
      message = 'Move back';
      distanceScore = Math.max(0, 1 - (areaRatio - optimalMax) / 0.35);
    }

    return {
      status,
      message,
      faceAreaRatio: areaRatio,
      optimalMin,
      optimalMax,
      distanceScore
    };
  }, []);

  // Calculate lighting guidance
  const calculateLightingGuidance = useCallback((): LightingGuidance => {
    if (!videoRef.current || !canvasRef.current) {
      return currentDetectionRef.current.lighting;
    }

    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return currentDetectionRef.current.lighting;

      const video = videoRef.current;
      canvas.width = Math.min(video.videoWidth, 320);
      canvas.height = Math.min(video.videoHeight, 240);
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      let totalBrightness = 0;
      let darkPixels = 0;
      let brightPixels = 0;
      const pixelCount = data.length / 4;

      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        const brightness = (r + g + b) / 3;

        totalBrightness += brightness;
        if (brightness < 60) darkPixels++;
        if (brightness > 200) brightPixels++;
      }

      const avgBrightness = totalBrightness / pixelCount;
      const hasGlare = brightPixels / pixelCount > 0.1;
      const hasShadows = darkPixels / pixelCount > 0.3;

      const optimalMin = 80;
      const optimalMax = 180;

      let status: LightingGuidance['status'];
      let message: string;
      let lightingScore: number;

      if (avgBrightness < optimalMin * 0.7) {
        status = 'too_dark';
        message = 'Add more light';
        lightingScore = avgBrightness / optimalMin;
      } else if (avgBrightness < optimalMin) {
        status = 'slightly_dark';
        message = 'Slightly dark';
        lightingScore = 0.8;
      } else if (avgBrightness <= optimalMax) {
        status = 'good';
        message = 'Good lighting';
        lightingScore = 1.0;
      } else if (avgBrightness <= optimalMax * 1.2) {
        status = 'slightly_bright';
        message = 'Slightly bright';
        lightingScore = 0.8;
      } else {
        status = 'too_bright';
        message = 'Too bright';
        lightingScore = Math.max(0, 1 - (avgBrightness - optimalMax) / 100);
      }

      return {
        status,
        message,
        brightness: avgBrightness,
        optimalMin,
        optimalMax,
        lightingScore,
        hasGlare,
        hasShadows
      };
    } catch (error) {
      console.error('[FacialRecognition] Lighting analysis failed:', error);
      return currentDetectionRef.current.lighting;
    }
  }, []);

  /**
   * Accumulate liveness evidence for the current session.
   *
   * Signals:
   *  - Blink via FaceLandmarker eyeBlink blendshapes (strong). A blink is a
   *    close→open transition, counted only when the closed phase is shorter
   *    than BLINK_MAX_DURATION_MS (real blinks are ~100–400ms).
   *  - Face presence duration (fallback support signal).
   *  - Micro-movement variance (fallback signal). LATCHED: once observed it
   *    stays true — instantaneous movement disappears when a compliant user
   *    holds still for capture, which is the worst time to measure it.
   */
  const calculateLiveness = useCallback((detection: Detection): LivenessState => {
    const currentLiveness = currentDetectionRef.current.liveness;
    const now = Date.now();

    let blinkCount = currentLiveness.blinkCount;
    let lastBlinkTime = currentLiveness.lastBlinkTime;

    // Adaptive blink state machine driven by the latest FaceLandmarker
    // blendshape score (null when the landmarker is off or sees no face).
    // Absolute thresholds miss most real blinks — the eyeBlink peak varies
    // widely (0.3-0.9) across cameras, lighting and users — so we calibrate a
    // session baseline (EMA of open-eye scores) and trip on relative
    // excursions instead.
    const blinkScore = latestBlinkScoreRef.current;
    if (blinkScore !== null) {
      const baseline = blinkBaselineRef.current;
      const closedThreshold = Math.min(
        FACIAL_RECOGNITION_CONFIG.BLINK_MAX_CLOSED_ABS,
        Math.max(FACIAL_RECOGNITION_CONFIG.BLINK_MIN_CLOSED_ABS, baseline + FACIAL_RECOGNITION_CONFIG.BLINK_CLOSED_DELTA)
      );
      const openThreshold = baseline + FACIAL_RECOGNITION_CONFIG.BLINK_OPEN_DELTA;

      if (!eyesClosedRef.current) {
        if (blinkScore >= closedThreshold) {
          eyesClosedRef.current = true;
          eyesClosedAtRef.current = now;
        } else {
          // Learn the baseline only while the eyes are open — blink peaks must
          // not inflate it. The cap keeps the closed threshold reachable for
          // squinters and low-contrast cameras.
          blinkBaselineRef.current = Math.min(
            0.45,
            baseline + FACIAL_RECOGNITION_CONFIG.BLINK_BASELINE_ALPHA * (blinkScore - baseline)
          );
        }
      } else if (blinkScore <= openThreshold) {
        eyesClosedRef.current = false;
        if (now - eyesClosedAtRef.current <= FACIAL_RECOGNITION_CONFIG.BLINK_MAX_DURATION_MS) {
          blinkCount += 1;
          lastBlinkTime = now;
          console.log('[FacialRecognition] Blink detected, session count:', blinkCount);
        }
      }
    }

    // Eyes held closed too long (noise / talking with eyes shut) — reset the
    // state so the next close can start a fresh candidate blink.
    if (eyesClosedRef.current && now - eyesClosedAtRef.current > FACIAL_RECOGNITION_CONFIG.BLINK_MAX_DURATION_MS) {
      eyesClosedRef.current = false;
    }

    // Continuous face presence. This runs only on frames where a face was
    // detected, so a gap longer than the tolerance means the face left the
    // frame (or tracking was lost) and presence starts over.
    let facePresenceMs = currentLiveness.facePresenceMs;
    const gapSinceLastSeen = now - lastFaceSeenAtRef.current;
    if (facePresentSinceRef.current === null || gapSinceLastSeen > FACIAL_RECOGNITION_CONFIG.FACE_PRESENCE_GAP_TOLERANCE_MS) {
      facePresentSinceRef.current = now;
      facePresenceMs = 0;
    } else {
      facePresenceMs = now - facePresentSinceRef.current;
    }
    lastFaceSeenAtRef.current = now;

    // Detect micro-movements from detection history (fallback liveness signal)
    const history = detectionHistoryRef.current;
    let frameMovement = false;

    if (history.length >= 5) {
      const recent = history.slice(-5);
      const positions = recent.map(h => ({ x: h.bounds.centerX, y: h.bounds.centerY }));
      
      let totalVariance = 0;
      for (let i = 1; i < positions.length; i++) {
        const dx = positions[i].x - positions[i - 1].x;
        const dy = positions[i].y - positions[i - 1].y;
        totalVariance += Math.sqrt(dx * dx + dy * dy);
      }
      
      frameMovement = totalVariance > FACIAL_RECOGNITION_CONFIG.MICRO_MOVEMENT_VARIANCE_THRESHOLD;
    }

    // Latch: once natural movement has been observed this session it stays.
    const microMovementDetected = currentLiveness.microMovementDetected || frameMovement;

    // Estimate head pose from bounding box position
    const box = detection.boundingBox;
    let poseHistory = currentLiveness.poseHistory;
    if (box && videoRef.current) {
      const videoWidth = videoRef.current.videoWidth;
      const videoHeight = videoRef.current.videoHeight;
      const centerX = box.originX + box.width / 2;
      const centerY = box.originY + box.height / 2;
      
      // Calculate head pose from bounding box position
      // Note: Mirrored video - invert yaw so user's right turn = negative yaw
      // Match Vue implementation exactly
      const pitch = (centerY / videoHeight - 0.5) * 60; // Estimate pitch
      const yaw = (0.5 - centerX / videoWidth) * 60;    // Inverted for mirror: right turn = negative

      const pose: HeadPose = {
        roll: 0,
        pitch,
        yaw,
        timestamp: now
      };

      poseHistory = [...poseHistory, pose];
      // Keep last 30 poses
      if (poseHistory.length > 30) {
        poseHistory.shift();
      }
    }

    // Facial depth (planarity) — a live face has a large landmark z-spread
    // relative to its width; a photo or screen replay is near-planar. Voted
    // over a short window so a single noisy frame can't latch it.
    const landmarks = latestLandmarksRef.current;
    let facialDepthOk = currentLiveness.facialDepthOk;
    let faceDepthRatio = currentLiveness.faceDepthRatio;
    if (landmarks && landmarks.length > 100) {
      let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
      for (const lm of landmarks) {
        if (lm.x < minX) minX = lm.x;
        if (lm.x > maxX) maxX = lm.x;
        if (lm.z < minZ) minZ = lm.z;
        if (lm.z > maxZ) maxZ = lm.z;
      }
      const width = maxX - minX;
      faceDepthRatio = width > 0 ? (maxZ - minZ) / width : 0;
      if (!facialDepthOk) {
        const votes = depthVotesRef.current;
        votes.push(faceDepthRatio >= FACIAL_RECOGNITION_CONFIG.FACE_DEPTH_MIN_RATIO);
        if (votes.length > FACIAL_RECOGNITION_CONFIG.FACE_DEPTH_VOTE_FRAMES) votes.shift();
        if (votes.length >= FACIAL_RECOGNITION_CONFIG.FACE_DEPTH_VOTE_FRAMES
            && votes.filter(Boolean).length >= FACIAL_RECOGNITION_CONFIG.FACE_DEPTH_VOTE_MIN) {
          facialDepthOk = true;
          console.log('[FacialRecognition] Facial depth latched (3D face, ratio', faceDepthRatio.toFixed(2) + ')');
        }
      }
    }

    // Non-rigid micro-motion — a photo moves every landmark identically; a live
    // face deforms slightly even when "still". Compare per-landmark
    // displacement between consecutive landmarker frames at a fixed subset.
    let nonRigidMotionDetected = currentLiveness.nonRigidMotionDetected;
    const prevLandmarks = prevLandmarksRef.current;
    if (landmarks && prevLandmarks && landmarks.length === prevLandmarks.length && !nonRigidMotionDetected) {
      nonRigidFrameCountRef.current += 1;
      // Compare only every 3rd landmarker frame so per-frame regression noise
      // doesn't dominate the displacement deltas.
      if (nonRigidFrameCountRef.current % 3 === 0) {
        const scale = videoRef.current?.videoWidth || 1280;
        const disps: number[] = [];
        for (const idx of NONRIGID_LANDMARK_INDICES) {
          const a = prevLandmarks[idx];
          const b = landmarks[idx];
          if (a && b) {
            disps.push(Math.hypot((b.x - a.x) * scale, (b.y - a.y) * scale));
          }
        }
        prevLandmarksRef.current = landmarks;
        if (disps.length >= 16) {
          const mean = disps.reduce((s, d) => s + d, 0) / disps.length;
          const variance = disps.reduce((s, d) => s + (d - mean) ** 2, 0) / disps.length;
          const cv = mean > 0 ? Math.sqrt(variance) / mean : 0;
          const isNonRigid = mean >= FACIAL_RECOGNITION_CONFIG.NONRIGID_MIN_MEAN_PX
            && cv >= FACIAL_RECOGNITION_CONFIG.NONRIGID_CV_THRESHOLD;
          const votes = nonRigidVotesRef.current;
          votes.push(isNonRigid);
          if (votes.length > FACIAL_RECOGNITION_CONFIG.NONRIGID_VOTE_FRAMES) votes.shift();
          if (votes.length >= FACIAL_RECOGNITION_CONFIG.NONRIGID_VOTE_FRAMES
              && votes.filter(Boolean).length >= FACIAL_RECOGNITION_CONFIG.NONRIGID_VOTE_MIN) {
            nonRigidMotionDetected = true;
            console.log('[FacialRecognition] Non-rigid facial motion latched (cv', cv.toFixed(2) + ')');
          }
        }
      }
    } else if (landmarks) {
      prevLandmarksRef.current = landmarks;
    }

    // Device motion (gyro/accelerometer) — latched by the devicemotion handler;
    // a hand-held phone always trembles, a propped device does not.
    const deviceMotionDetected = currentLiveness.deviceMotionDetected || deviceMotionDetectedRef.current;

    return {
      ...currentLiveness,
      blinkCount,
      lastBlinkTime,
      poseHistory,
      microMovementDetected,
      facePresenceMs,
      landmarkerActive: !!landmarkerRef.current,
      facialDepthOk,
      faceDepthRatio,
      nonRigidMotionDetected,
      deviceMotionDetected,
      deviceMotionLevel: deviceMotionLevelRef.current
    };
  }, []);

    // Process detections
  const processDetections = useCallback((detections: Detection[]) => {
    if (!detections || detections.length === 0) {
      // Only update if currently detected (to avoid spam)
      if (currentDetectionRef.current.detected) {
        console.log('[FacialRecognition] No face detected in frame');
      }
      setCurrentDetection(prev => {
        if (!prev.detected) {
          currentDetectionRef.current = prev;
          return prev;
        }

        const nextDetection: FaceDetectionResult = {
          ...prev,
          detected: false,
          bounds: null,
          confidence: 0,
          metrics: {
            presence: 0,
            stability: 0,
            quality: 0,
            liveness: 0,
            distance: 0,
            confidence: 0
          },
          readyToCapture: false
        };

        currentDetectionRef.current = nextDetection;
        lastPublishedDetectionAtRef.current = performance.now();
        return nextDetection;
      });
      return;
    }

    // Use first detection
    const detection = detections[0];
    const box = detection.boundingBox;

    if (!box) {
      console.warn('[FacialRecognition] Detection has no bounding box');
      return;
    }

    // MediaPipe returns PIXEL coordinates already, NOT normalized (0-1)
    // Debug: Log raw box values to confirm
    if (Math.random() < 0.01) {
      console.log('[FacialRecognition] Raw box from MediaPipe:', {
        originX: box.originX,
        originY: box.originY,
        width: box.width,
        height: box.height,
        videoWidth: videoRef.current?.videoWidth || 1280,
        videoHeight: videoRef.current?.videoHeight || 720
      });
    }

    // Use box values directly as they're already in pixels
    const bounds: FaceBounds = {
      x: box.originX,
      y: box.originY,
      width: box.width,
      height: box.height,
      centerX: box.originX + box.width / 2,
      centerY: box.originY + box.height / 2
    };

    const confidence = detection.categories?.[0]?.score || 0;

    // Add to history
    const timestamp = performance.now();
    detectionHistoryRef.current.push({ timestamp, bounds, confidence });

    // Clean old history
    const cutoff = timestamp - FACIAL_RECOGNITION_CONFIG.HISTORY_DURATION_MS;
    detectionHistoryRef.current = detectionHistoryRef.current.filter(h => h.timestamp > cutoff);

    // Get keypoints for pose estimation
    const keypoints = detection.keypoints || [];

    // Calculate metrics (now includes pose from keypoints)
    const metrics = calculateQualityMetrics(bounds, confidence, keypoints);
    const liveness = calculateLiveness(detection);
    const distance = calculateDistanceGuidance(bounds);
    const lighting = calculateLightingGuidance();

    // Log occasionally for debugging (every 30 frames = ~2 seconds)
    if (Math.random() < 0.033) {
      console.log('[FacialRecognition] Detection metrics:', {
        bounds: `${Math.round(bounds.width)}x${Math.round(bounds.height)}`,
        confidence: Math.round(confidence * 100) + '%',
        presence: Math.round(metrics.presence * 100) + '%',
        stability: Math.round(metrics.stability * 100) + '%',
        quality: Math.round(metrics.quality * 100) + '%',
        pose: metrics.pose ? `yaw:${metrics.pose.yaw.toFixed(0)}° pitch:${metrics.pose.pitch.toFixed(0)}° score:${(metrics.pose.poseScore * 100).toFixed(0)}%` : 'N/A',
        distance: distance.status,
        lighting: lighting.status
      });
    }

    // Check if ready to capture
    const readyToCapture = 
      metrics.presence >= FACIAL_RECOGNITION_CONFIG.MIN_PRESENCE &&
      metrics.stability >= FACIAL_RECOGNITION_CONFIG.MIN_STABILITY &&
      metrics.quality >= FACIAL_RECOGNITION_CONFIG.MIN_QUALITY &&
      metrics.distance >= FACIAL_RECOGNITION_CONFIG.MIN_DISTANCE &&
      metrics.confidence >= FACIAL_RECOGNITION_CONFIG.MIN_CONFIDENCE;

    const now = performance.now();

    setCurrentDetection(prev => {
      const nextDetection: FaceDetectionResult = {
        detected: true,
        bounds,
        confidence,
        landmarks: detection.keypoints || [],
        metrics,
        liveness,
        distance,
        lighting,
        readyToCapture
      };

      currentDetectionRef.current = nextDetection;

      if (!hasMeaningfulDetectionChange(prev, nextDetection)) {
        return prev;
      }

      if (
        !shouldPublishDetectionImmediately(prev, nextDetection)
        && now - lastPublishedDetectionAtRef.current < DETECTION_STATE_PUBLISH_INTERVAL_MS
      ) {
        return prev;
      }

      lastPublishedDetectionAtRef.current = now;

      return nextDetection;
    });
  }, [calculateQualityMetrics, calculateLiveness, calculateDistanceGuidance, calculateLightingGuidance]);

  // Start detection
  const startDetection = useCallback((canvas: HTMLCanvasElement) => {
    console.log('[FacialRecognition] Starting detection...');
    
    // Set canvas ref first
    canvasRef.current = canvas;

    // Check if everything is ready (use refs, not state!)
    const ready = detectorRef.current && videoRef.current && streamRef.current;
    
    if (!ready) {
      console.warn('[FacialRecognition] Cannot start detection - not ready', {
        hasDetector: !!detectorRef.current,
        hasVideo: !!videoRef.current,
        hasStream: !!streamRef.current,
        hasCanvas: !!canvas
      });
      return;
    }

    if (isDetectingRef.current) {
      console.log('[FacialRecognition] Detection already running');
      return;
    }

    console.log('[FacialRecognition] All systems ready, starting detection loop');
    isDetectingRef.current = true;
    setIsDetecting(true);
    
    // Start the loop immediately (don't wait for state update)
    animationFrameRef.current = requestAnimationFrame(function loop() {
      if (!videoRef.current || !detectorRef.current) {
        console.log('[FacialRecognition] Loop stopped - missing refs');
        return;
      }

      const now = performance.now();
      const frameInterval = 1000 / FACIAL_RECOGNITION_CONFIG.TARGET_FPS;

      if (now - lastDetectionTimeRef.current >= frameInterval) {
        lastDetectionTimeRef.current = now;

        (async () => {
          try {
            if (!videoRef.current || videoRef.current.readyState < 2) {
              animationFrameRef.current = requestAnimationFrame(loop);
              return;
            }

            if (videoRef.current.videoWidth === 0 || videoRef.current.videoHeight === 0) {
              animationFrameRef.current = requestAnimationFrame(loop);
              return;
            }

            if (!detectorRef.current) {
              animationFrameRef.current = requestAnimationFrame(loop);
              return;
            }

            const detections = await detectorRef.current.detect(videoRef.current);
            consecutiveErrorsRef.current = 0;
            const detectionCount = detections.detections?.length || 0;

            if (detectionCount > 0) {
              zeroDetectionStreakRef.current = 0;
            } else {
              zeroDetectionStreakRef.current += 1;

              if (
                isCapacitor &&
                detectorDelegateRef.current === 'GPU' &&
                !runtimeFallbackAttemptedRef.current &&
                !runtimeRecoveryInFlightRef.current &&
                zeroDetectionStreakRef.current >= ZERO_DETECTION_CPU_RECOVERY_FRAMES
              ) {
                runtimeFallbackAttemptedRef.current = true;
                console.warn(
                  '[FacialRecognition] GPU detector returned zero faces for an extended period in Capacitor; retrying with CPU delegate'
                );

                runtimeRecoveryInFlightRef.current = (async () => {
                  try {
                    disposeCurrentDetector();
                    setIsInitialized(false);
                    await initializeDetector(detectorWasmPathRef.current || undefined, 'CPU');
                    zeroDetectionStreakRef.current = 0;
                    console.log('[FacialRecognition] ✅ Runtime recovery to CPU delegate completed');
                  } catch (recoveryError) {
                    console.error('[FacialRecognition] CPU delegate recovery failed:', recoveryError);
                  } finally {
                    runtimeRecoveryInFlightRef.current = null;
                  }
                })();

                await runtimeRecoveryInFlightRef.current;
                animationFrameRef.current = requestAnimationFrame(loop);
                return;
              }
            }
            
            // FaceLandmarker pass for blink/liveness blendshapes — only when a
            // caller enabled liveness. Runs at the same target FPS as the
            // detector; errors here must not break face detection.
            if (detectionCount > 0 && livenessEnabledRef.current && landmarkerRef.current) {
              try {
                // detectForVideo requires strictly increasing timestamps.
                const landmarkerTimestamp = Math.max(performance.now(), lastLandmarkerTimestampRef.current + 1);
                const landmarkerResult = landmarkerRef.current.detectForVideo(videoRef.current, landmarkerTimestamp);
                lastLandmarkerTimestampRef.current = landmarkerTimestamp;

                const blendshapeCategories = landmarkerResult.faceBlendshapes?.[0]?.categories;
                if (blendshapeCategories && blendshapeCategories.length > 0) {
                  const leftEye = blendshapeCategories.find(c => c.categoryName === 'eyeBlinkLeft')?.score ?? 0;
                  const rightEye = blendshapeCategories.find(c => c.categoryName === 'eyeBlinkRight')?.score ?? 0;
                  latestBlinkScoreRef.current = (leftEye + rightEye) / 2;
                } else {
                  latestBlinkScoreRef.current = null;
                }

                // Full landmark set for the passive depth/non-rigid signals.
                const faceLms = landmarkerResult.faceLandmarks?.[0];
                latestLandmarksRef.current = faceLms && faceLms.length > 0 ? faceLms : null;
                if (!latestLandmarksRef.current) {
                  // Stale prev landmarks would look like a huge non-rigid jump
                  // on the next valid frame.
                  prevLandmarksRef.current = null;
                }
                landmarkerErrorStreakRef.current = 0;
              } catch (landmarkerError) {
                latestLandmarksRef.current = null;
                prevLandmarksRef.current = null;
                landmarkerErrorStreakRef.current += 1;
                if (landmarkerErrorStreakRef.current <= 3 || landmarkerErrorStreakRef.current % 30 === 0) {
                  console.warn('[FacialRecognition] FaceLandmarker frame error:', landmarkerError);
                }
                if (landmarkerErrorStreakRef.current >= 10) {
                  console.warn('[FacialRecognition] FaceLandmarker disabled after repeated errors — falling back to movement liveness');
                  landmarkerUnavailableRef.current = true;
                  disposeLandmarker();
                  const prev = currentDetectionRef.current;
                  const nextDetection: FaceDetectionResult = {
                    ...prev,
                    liveness: { ...prev.liveness, landmarkerActive: false }
                  };
                  currentDetectionRef.current = nextDetection;
                  setCurrentDetection(nextDetection);
                }
              }
            } else if (detectionCount === 0) {
              latestBlinkScoreRef.current = null;
              latestLandmarksRef.current = null;
              prevLandmarksRef.current = null;
            }

            // Log detection results periodically for debugging
            if (Math.random() < 0.05) { // ~5% of frames
              console.log('[FacialRecognition] Detection result:', {
                count: detectionCount,
                hasDetections: detectionCount > 0,
                delegate: detectorDelegateRef.current,
                zeroDetectionStreak: zeroDetectionStreakRef.current,
                firstDetection: detections.detections?.[0] ? {
                  hasBox: !!detections.detections[0].boundingBox,
                  confidence: detections.detections[0].categories?.[0]?.score
                } : null
              });
            }
            
            processDetections(detections.detections);

            animationFrameRef.current = requestAnimationFrame(loop);

          } catch (error: any) {
            consecutiveErrorsRef.current++;
            console.error('[FacialRecognition] Detection error:', error);

            // GPU delegate can produce a broken detector that throws on .detect()
            // (e.g. 'activeTexture' undefined) because MediaPipe doesn't throw on
            // GPU init failure. Fall back to CPU delegate once before giving up.
            // A working GPU detector should never throw on .detect(), so any error
            // on GPU delegate is treated as a broken detector.
            const isGpuBreakage = detectorDelegateRef.current === 'GPU'
              && !runtimeFallbackAttemptedRef.current
              && !runtimeRecoveryInFlightRef.current;

            if (isGpuBreakage) {
              runtimeFallbackAttemptedRef.current = true;
              console.warn('[FacialRecognition] GPU detector broken (activeTexture error), falling back to CPU delegate');

              runtimeRecoveryInFlightRef.current = (async () => {
                try {
                  disposeCurrentDetector();
                  setIsInitialized(false);
                  await initializeDetector(detectorWasmPathRef.current || undefined, 'CPU');
                  consecutiveErrorsRef.current = 0;
                  console.log('[FacialRecognition] ✅ Error recovery to CPU delegate completed');
                } catch (recoveryError) {
                  console.error('[FacialRecognition] CPU delegate recovery failed:', recoveryError);
                } finally {
                  runtimeRecoveryInFlightRef.current = null;
                }
              })();

              await runtimeRecoveryInFlightRef.current;
              animationFrameRef.current = requestAnimationFrame(loop);
              return;
            }

            if (consecutiveErrorsRef.current < 5) {
              animationFrameRef.current = requestAnimationFrame(loop);
            } else {
              console.error('[FacialRecognition] Too many errors, stopping detection');
              isDetectingRef.current = false;
              setIsDetecting(false);
            }
          }
        })();
      } else {
        animationFrameRef.current = requestAnimationFrame(loop);
      }
    });
  }, [disposeCurrentDetector, disposeLandmarker, initializeDetector, isCapacitor, processDetections]);

  // Stop detection
  const stopDetection = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    isDetectingRef.current = false;
    setIsDetecting(false);
    zeroDetectionStreakRef.current = 0;
    lastPublishedDetectionAtRef.current = 0;
    console.log('[FacialRecognition] Detection stopped');
  }, []);

  // Switch camera between front and back
  const switchCamera = useCallback(async (videoElement?: HTMLVideoElement): Promise<void> => {
    const newFacingMode: CameraFacingMode = facingMode === 'user' ? 'environment' : 'user';
    console.log(`[FacialRecognition] Switching camera from ${facingMode} to ${newFacingMode}`);
    
    // Stop current detection and camera
    stopDetection();
    stopCamera();
    
    // Update facing mode state and save to localStorage
    setFacingMode(newFacingMode);
    if (typeof window !== 'undefined') {
      localStorage.setItem('smartclock_camera_preference', newFacingMode);
      console.log('[FacialRecognition] Saved camera preference to localStorage:', newFacingMode);
    }
    
    // If video element provided, reinitialize camera
    if (videoElement || videoRef.current) {
      const video = videoElement || videoRef.current;
      if (video) {
        // Small delay to ensure state is updated
        await new Promise(resolve => setTimeout(resolve, 100));
        
        try {
          // Request new camera with updated facing mode
          const constraints = {
            video: {
              facingMode: newFacingMode,
              width: { ideal: 1280, min: 640 },
              height: { ideal: 720, min: 480 },
              aspectRatio: { ideal: 16 / 9 }
            },
            audio: false
          };
          
          console.log('[FacialRecognition] Requesting camera with new constraints:', constraints);
          streamRef.current = await navigator.mediaDevices.getUserMedia(constraints);
          await normalizeCameraTrackZoom(streamRef.current.getVideoTracks()[0]);
          
          video.srcObject = streamRef.current;
          // Handle AbortError gracefully - happens when autoPlay conflicts with manual play()
          try {
            await video.play();
          } catch (playError: any) {
            if (playError.name === 'AbortError') {
              console.log('[FacialRecognition] Play interrupted during switch (autoPlay conflict) - video will continue via autoPlay');
            } else {
              throw playError;
            }
          }
          
          console.log('[FacialRecognition] Camera switched successfully to', newFacingMode);
        } catch (err: any) {
          console.error('[FacialRecognition] Failed to switch camera:', err);
          // Try fallback with basic constraints
          try {
            const basicConstraints = { video: { facingMode: newFacingMode }, audio: false };
            streamRef.current = await navigator.mediaDevices.getUserMedia(basicConstraints);
            await normalizeCameraTrackZoom(streamRef.current.getVideoTracks()[0]);
            video.srcObject = streamRef.current;
            // Handle AbortError gracefully
            try {
              await video.play();
            } catch (playError: any) {
              if (playError.name !== 'AbortError') {
                throw playError;
              }
            }
          } catch (fallbackErr) {
            console.error('[FacialRecognition] Fallback also failed:', fallbackErr);
            throw new Error(`Failed to switch to ${newFacingMode === 'user' ? 'front' : 'back'} camera`);
          }
        }
      }
    }
  }, [facingMode, normalizeCameraTrackZoom, stopDetection, stopCamera]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCamera();
      stopDetection();
      disposeLandmarker();
      if (motionListenerAttachedRef.current) {
        window.removeEventListener('devicemotion', handleDeviceMotion);
        motionListenerAttachedRef.current = false;
      }
    };
  }, [disposeLandmarker, stopCamera, stopDetection, handleDeviceMotion]);

  return {
    isInitialized,
    isDetecting,
    currentDetection,
    facingMode,
    // Camera permission state
    cameraPermission,
    permissionError,
    isCapacitor,
    // Functions
    initializeCamera,
    initializeDetector,
    startDetection,
    stopDetection,
    stopCamera,
    switchCamera,
    checkCameraPermission,
    openCameraSettings,
    // Liveness (FaceLandmarker blendshapes + passive motion/depth — lazy, opt-in)
    enableLiveness,
    resetLiveness,
    requestMotionSensorPermission,
    config: FACIAL_RECOGNITION_CONFIG
  };
}
